import React, { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import {
  FormSection,
  FormField,
  SectionHeader,
  SectionBody,
  SectionFooter,
  Button,
  Link,
  Input,
  InputLabel,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
} from './auth-ui';

function CustomForgotPassword({ theme, authData = {}, changeState, onError }) {
  const [username, setUsername] = useState(authData.username || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  // Set once Cognito confirms it sent a code, which is what swaps the form from
  // "who are you" to "here is the code and my new password".
  const [delivery, setDelivery] = useState(null);
  const [pending, setPending] = useState(false);

  const codeSent = Boolean(delivery || authData.username);

  const send = async () => {
    setPending(true);
    try {
      // v3's Auth.forgotPassword returned CodeDeliveryDetails at the top level;
      // v6 returns a nextStep and only carries the details when the flow needs
      // a code (a pool configured to reset without one returns DONE).
      const { nextStep } = await resetPassword({ username });
      setDelivery(nextStep.codeDeliveryDetails || nextStep.resetPasswordStep);
    } catch (err) {
      onError(err);
    }
    setPending(false);
  };

  const submit = async () => {
    setPending(true);
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: password,
      });
      setDelivery(null);
      changeState('signIn', { username });
    } catch (err) {
      onError(err);
      setPending(false);
    }
  };

  const sendView = (
    <div>
      <FormField theme={theme}>
        <Input
          value={username}
          autoFocus={true}
          placeholder={I18n.get('Enter your username')}
          theme={theme}
          name="username"
          onChange={(e) => setUsername(e.target.value)}
          data-test="username-input"
          autoCapitalize="off"
        />
      </FormField>
    </div>
  );

  const submitView = (
    <div>
      <FormField theme={theme}>
        <InputLabel theme={theme}>{I18n.get('Code')} *</InputLabel>
        <Input
          placeholder={I18n.get('Code')}
          theme={theme}
          name="code"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-test="forgot-password-code-input"
        />
      </FormField>
      <FormField theme={theme}>
        <InputLabel theme={theme}>{I18n.get('New Password')} *</InputLabel>
        <Input
          placeholder={I18n.get('New Password')}
          theme={theme}
          type="password"
          name="password"
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-test="forgot-password-new-password-input"
        />
      </FormField>
    </div>
  );

  return (
    <FormSection theme={theme} data-test="forgot-password-section">
      <SectionHeader theme={theme} data-test="forgot-password-header-section">
        {I18n.get('Reset your password')}
      </SectionHeader>
      <SectionBody theme={theme} data-test="forgot-password-body-section">
        {codeSent ? submitView : sendView}
      </SectionBody>
      <SectionFooter theme={theme}>
        <SectionFooterPrimaryContent theme={theme}>
          {codeSent ? (
            <Button
              theme={theme}
              onClick={submit}
              disabled={pending}
              data-test="forgot-password-submit-button"
            >
              {I18n.get('Submit')}
            </Button>
          ) : (
            <Button
              theme={theme}
              onClick={send}
              disabled={pending}
              data-test="forgot-password-send-code-button"
            >
              {I18n.get('Send Code')}
            </Button>
          )}
        </SectionFooterPrimaryContent>
        <SectionFooterSecondaryContent theme={theme}>
          {codeSent ? (
            <Link
              theme={theme}
              onClick={send}
              data-test="forgot-password-resend-code-link"
            >
              {I18n.get('Resend Code')}
            </Link>
          ) : (
            <Link
              theme={theme}
              onClick={() => changeState('signIn')}
              data-test="forgot-password-back-to-sign-in-link"
            >
              {I18n.get('Back to Sign In')}
            </Link>
          )}
        </SectionFooterSecondaryContent>
      </SectionFooter>
    </FormSection>
  );
}

export default CustomForgotPassword;
